(function (root, factory) {
  const controlsModule = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = controlsModule;
  }

  if (root) {
    root.MarineMaxAIChatControls = controlsModule;
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  function createControlsController(options = {}) {
    const input = options.input;
    const sendButton = options.sendButton;
    const suggestionContainer = options.suggestionContainer || null;
    const suggestionList =
      options.suggestionList ||
      (suggestionContainer
        ? suggestionContainer.querySelector('.ai-chat__suggest-list')
        : null);
    const moreSuggestionsButton =
      options.moreSuggestionsButton ||
      (suggestionContainer
        ? suggestionContainer.querySelector('.qi-chat__more-suggerst')
        : null);
    const viewQuoteButton = options.viewQuoteButton || null;
    const windowRef = options.window || null;
    const mobileMedia =
      options.mobileMedia ||
      (windowRef && typeof windowRef.matchMedia === 'function'
        ? windowRef.matchMedia('(max-width: 1024px)')
        : null);

    if (!input || !sendButton) {
      throw new Error('AI Chat input and send button are required by the controls module');
    }

    const inputShell = options.inputShell || input.closest('.ai-chat__input');
    const onInput = options.onInput || (() => {});
    const onInputReset = options.onInputReset || (() => {});
    const onSend = options.onSend || (() => {});
    const onSuggestion = options.onSuggestion || (() => {});
    const onViewQuote = options.onViewQuote || (() => {});
    let isBound = false;
    let mediaListenerBound = false;
    let moreSuggestionsVisible = false;

    function getSuggestionButtons() {
      return suggestionList
        ? Array.from(suggestionList.querySelectorAll('.ai-chat__suggest-btn'))
        : [];
    }

    function getExtraSuggestions() {
      return getSuggestionButtons().filter(
        (button) =>
          button.dataset.aiPlacement === 'extra' && !button.hidden,
      );
    }

    function getResponsiveActions() {
      return getSuggestionButtons().filter(
        (button) =>
          button.dataset.aiShowDesktop !== undefined ||
          button.dataset.aiShowMobile !== undefined,
      );
    }

    function syncPurchaseCtaFallback() {
      const showFallback = !mobileMedia || mobileMedia.matches;
      getSuggestionButtons().forEach((button) => {
        if (button.dataset.aiPurchaseCtaFallback !== 'true') return;
        button.hidden = !showFallback;
      });
      getResponsiveActions().forEach((button) => {
        button.hidden = showFallback
          ? button.dataset.aiShowMobile === 'false'
          : button.dataset.aiShowDesktop === 'false';
      });
    }

    function sortSuggestions() {
      if (!suggestionList) return;
      getSuggestionButtons()
        .sort((firstButton, secondButton) => {
          const firstPlacement =
            firstButton.dataset.aiPlacement === 'extra' ? 1 : 0;
          const secondPlacement =
            secondButton.dataset.aiPlacement === 'extra' ? 1 : 0;
          if (firstPlacement !== secondPlacement) {
            return firstPlacement - secondPlacement;
          }

          const firstOrder = Number(firstButton.dataset.aiOrder || 999);
          const secondOrder = Number(secondButton.dataset.aiOrder || 999);
          return firstOrder - secondOrder;
        })
        .forEach((button) => suggestionList.appendChild(button));
    }

    function updateLastSuggestionClass() {
      const allButtons = getSuggestionButtons();
      if (moreSuggestionsButton) allButtons.push(moreSuggestionsButton);
      allButtons.forEach((button) => button.classList.remove('last-suggest'));

      const visibleButtons = allButtons.filter(
        (button) =>
          !button.hidden && !button.classList.contains('hide'),
      );
      const lastVisibleButton = visibleButtons[visibleButtons.length - 1];
      if (lastVisibleButton) lastVisibleButton.classList.add('last-suggest');
    }

    function syncSuggestions() {
      syncPurchaseCtaFallback();
      const extraSuggestions = getExtraSuggestions();
      const hasExtraSuggestions = extraSuggestions.length > 0;

      if (moreSuggestionsButton) {
        moreSuggestionsButton.hidden = !hasExtraSuggestions;
        moreSuggestionsButton.style.display = hasExtraSuggestions ? 'flex' : 'none';
        moreSuggestionsButton.textContent = moreSuggestionsVisible
          ? 'Hide suggestions'
          : 'More suggestions';
      }
      extraSuggestions.forEach((button) => {
        button.classList.toggle('hide', !moreSuggestionsVisible);
      });
      updateLastSuggestionClass();
    }

    function handleViewportChange() {
      syncSuggestions();
    }

    function bindMediaListener() {
      if (!mobileMedia || mediaListenerBound) return;
      if (typeof mobileMedia.addEventListener === 'function') {
        mobileMedia.addEventListener('change', handleViewportChange);
      } else if (typeof mobileMedia.addListener === 'function') {
        mobileMedia.addListener(handleViewportChange);
      } else {
        return;
      }
      mediaListenerBound = true;
    }

    function unbindMediaListener() {
      if (!mobileMedia || !mediaListenerBound) return;
      if (typeof mobileMedia.removeEventListener === 'function') {
        mobileMedia.removeEventListener('change', handleViewportChange);
      } else if (typeof mobileMedia.removeListener === 'function') {
        mobileMedia.removeListener(handleViewportChange);
      }
      mediaListenerBound = false;
    }

    function toggleMoreSuggestions() {
      moreSuggestionsVisible = !moreSuggestionsVisible;
      syncSuggestions();
    }

    function handleSend(event) {
      event.preventDefault();
      event.stopPropagation();
      const message = String(input.value || '').trim();
      if (!message) return;

      onSend(message);
      onInputReset();
    }

    function handleInputKeydown(event) {
      if (event.key !== 'Enter' || event.shiftKey) return;

      event.preventDefault();
      event.stopPropagation();
      sendButton.click();
    }

    function handleInput() {
      onInput();
    }

    function handleInputFocus() {
      if (inputShell) inputShell.classList.add('focus');
    }

    function handleInputBlur() {
      if (inputShell) inputShell.classList.remove('focus');
    }

    function handleSuggestion(event) {
      const button = event.target.closest('button');
      if (!button || !suggestionContainer.contains(button)) return;

      event.preventDefault();
      event.stopPropagation();
      if (button.classList.contains('qi-chat__more-suggerst')) {
        toggleMoreSuggestions();
        return;
      }
      onSuggestion(button);
    }

    function handleViewQuote(event) {
      event.preventDefault();
      event.stopPropagation();
      onViewQuote();
    }

    function bind() {
      if (isBound) return;

      sortSuggestions();
      syncSuggestions();
      bindMediaListener();
      sendButton.addEventListener('click', handleSend);
      input.addEventListener('keydown', handleInputKeydown);
      input.addEventListener('input', handleInput);
      input.addEventListener('focus', handleInputFocus);
      input.addEventListener('blur', handleInputBlur);
      if (suggestionContainer) {
        suggestionContainer.addEventListener('click', handleSuggestion);
      }
      if (viewQuoteButton) {
        viewQuoteButton.addEventListener('click', handleViewQuote);
      }
      isBound = true;
    }

    function destroy() {
      if (!isBound) return;

      sendButton.removeEventListener('click', handleSend);
      input.removeEventListener('keydown', handleInputKeydown);
      input.removeEventListener('input', handleInput);
      input.removeEventListener('focus', handleInputFocus);
      input.removeEventListener('blur', handleInputBlur);
      if (suggestionContainer) {
        suggestionContainer.removeEventListener('click', handleSuggestion);
      }
      if (viewQuoteButton) {
        viewQuoteButton.removeEventListener('click', handleViewQuote);
      }
      unbindMediaListener();
      isBound = false;
    }

    function setDisabled(disabled) {
      const controlsDisabled = Boolean(disabled);
      sendButton.disabled = controlsDisabled;
      sendButton.style.opacity = controlsDisabled ? '0.7' : '1';
      sendButton.style.cursor = controlsDisabled ? 'not-allowed' : '';

      if (!suggestionContainer) return;
      suggestionContainer.querySelectorAll('button').forEach((button) => {
        button.disabled = controlsDisabled;
        button.style.opacity = controlsDisabled ? '0.7' : '1';
        button.style.cursor = controlsDisabled ? 'not-allowed' : '';
      });
    }

    bind();

    return Object.freeze({
      bind,
      destroy,
      get isBound() {
        return isBound;
      },
      setDisabled,
      syncSuggestions,
    });
  }

  return Object.freeze({
    createControlsController,
  });
});
